import { BotFlow } from '@feedyou-ai/feedbot-schema'

import { BotState, EditingUsers } from './types'
import { isEditingUserSelector } from './hooks/useIsEditingUser'
import { RootState } from '../../reducers'
import { getSocketId } from './socketServerConnectionService'
import { diffPatcher } from './diffService'
import { DISCARD_CHANGES } from '../../constants/actionTypes'
import { latestVersionFlowSelector } from '../../features/serverBots/selectors'
import { botFlowSelector } from '../flow/selectors/botFlowSelector'
import { roomStateSelector } from './selectors'

export function wasFlowModified(
  previousFlow: BotFlow | undefined,
  nextFlow: BotFlow | undefined
): boolean {
  const delta = diffPatcher.diff(previousFlow, nextFlow)
  return !!delta
}

export function isAfterSaveAction(actionType: string): boolean {
  if (actionType === 'SAVE_BOT_SUCCESS') {
    return true
  }

  if (actionType === 'APPLY_DIFF') {
    return true
  }
  return false
}

export function isDiscardhangesAction(actionType: string): boolean {
  if (DISCARD_CHANGES === actionType) {
    return true
  }
  return false
}

export function isDifferentVersion(
  originalVersion: BotFlow | undefined,
  flow: BotFlow | undefined
): boolean {
  return originalVersion?.version !== flow?.version
}

export function isChangingVersion(
  previousFlow: BotFlow | undefined,
  nextFlow: BotFlow | undefined
): boolean {
  return previousFlow?.version !== nextFlow?.version
}

export function hasChangesToEmit(
  previousFlow: BotFlow | undefined,
  nextFlow: BotFlow | undefined,
  actionType: string
): boolean {
  if (isDiscardhangesAction(actionType)) {
    return true
  }

  if (wasFlowModified(previousFlow, nextFlow)) {
    return true
  }

  return false
}

function generateDiff(state: RootState) {
  return diffPatcher.diff(latestVersionFlowSelector(state), botFlowSelector(state))
}

export function createBotState(state: RootState): BotState | undefined {
  const roomState = roomStateSelector(state)
  const flow = botFlowSelector(state)

  if (!flow || !roomState) {
    return undefined
  }

  const delta = generateDiff(state)

  if (!delta) {
    return {
      diff: JSON.stringify(delta),
      authorSocketId: getSocketId(),
      editingUsers: undefined,
      sequenceNumber: roomState.botState.sequenceNumber,
      subSequenceNumber: roomState.botState.subSequenceNumber,
      botTester: roomState.botState.botTester
    }
  }

  return {
    diff: JSON.stringify(delta),
    authorSocketId: getSocketId(),
    botVersion: flow.version,
    lastUpdated: Date.now(),
    editingUsers: createEditingUsers(state),
    sequenceNumber: roomState.botState.sequenceNumber,
    subSequenceNumber: roomState.botState.subSequenceNumber,
    botTester: roomState.botState.botTester
  }
}

export function createEditingUsers(state: Pick<RootState, 'socketServer' | 'user'>): EditingUsers {
  if (!state.user?.info?.id) {
    return {}
  }

  if (!state.socketServer.roomState?.botState?.editingUsers) {
    return {
      [state.user.info.id]: {
        id: state.user.info.id,
        name: state.user.info.name,
        email: state.user.info.email,
        picture: state.user.info.picture
      }
    }
  }

  return state.socketServer.roomState?.botState?.editingUsers
}

export function isEditingAllowed(state: RootState): boolean {
  if (!botFlowSelector(state)?.id) {
    return false
  }
  if (!state.socketServer.roomState) {
    return false
  }
  if (!roomStateSelector(state)?.botState?.diff) {
    return true
  }
  if (isEditingUserSelector(state)) {
    return true
  }
  return false
}
